import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import type { Invoice, TenantId } from '@etiquetachef/types';
import { InvoiceStatusBadge } from '@/components/InvoiceStatusBadge';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { PaymentTable } from '@/components/PaymentTable';
import { formatDate } from '@/lib/format';
import {
  subscribeInvoice,
  subscribePaymentsForInvoice,
  type DocResult,
  type PaymentListResult,
} from '@/lib/firestoreTenantDetail';
import { InvoiceActions } from '@/features/billing/InvoiceActions';

/**
 * Tela `/billing/invoices/:tenantId/:invoiceId`: detalhe de uma fatura, com
 * valores, ações e os pagamentos ligados a ela.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200 (Sprint 16 —
 * rotas /billing/* + sub-menu Cobrança).
 *
 * Contrato: dois listeners — `subscribeInvoice` (doc da fatura;
 * `NOT_FOUND`/`ERROR` viram mensagem) e `subscribePaymentsForInvoice`
 * (`where invoice_id ==` + `orderBy created_at desc`). Ações em
 * `InvoiceActions` (mesmas callables e capabilities da `InvoicesPage`:
 * `recordManualPayment`, `cancelInvoice`, `resendInvoiceEmail`,
 * `getInvoiceDocumentUrl`). A tela não grava direto.
 *
 * Usado por: `routes/router.tsx` (rota `billing/invoices/:tenantId/:invoiceId`,
 * dentro do `RequireSuperAdmin`); sem item de menu — chega-se por
 * `InvoicesPage`, `PendingPage`, `InvoiceActions`, `TenantFinanceTab`,
 * `TransactionsTable` e `RefundsTable`. Só `apps/admin` (web). Spec:
 * `docs/screens/billing-invoices.md`.
 *
 * Arquitetura: regras `match /invoices/{invoiceId}` e
 * `match /payments/{paymentId}` sob `tenants/{tenantId}` (Lê: membro ou
 * super-admin; Escreve: só servidor). A query dos pagamentos usa o índice
 * composto `payments (invoice_id ASC, created_at DESC)`.
 *
 * Dados: `tenants/{tid}/invoices/{invoiceId}` (`InvoiceSchema`):
 *   { "invoice_number": "INV-000123", "status": "PENDING", "currency": "BRL",
 *     "subtotal_amount": 34800, "tax_amount": 0, "discount_amount": 0,
 *     "total_amount": 34800, "amount_paid": 0, "amount_due": 34800,
 *     "issued_at": 1780000000000, "due_at": 1780600000000, "paid_at": null }
 * Valores em CENTAVOS (o `MoneyDisplay` divide por 100); datas em ms epoch;
 * `paid_at` `null` enquanto não pago (exibe "—").
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function InvoiceDetailPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { tenantId, invoiceId } = useParams<{ tenantId: string; invoiceId: string }>();
  const [invoice, setInvoice] = useState<DocResult<Invoice>>({ kind: 'LOADING' });
  const [payments, setPayments] = useState<PaymentListResult>({ kind: 'LOADING' });

  useEffect(() => {
    if (!tenantId || !invoiceId) return;
    setInvoice({ kind: 'LOADING' });
    return subscribeInvoice(tenantId, invoiceId, setInvoice);
  }, [tenantId, invoiceId]);

  useEffect(() => {
    if (!tenantId || !invoiceId) return;
    setPayments({ kind: 'LOADING' });
    return subscribePaymentsForInvoice(tenantId, invoiceId, setPayments);
  }, [tenantId, invoiceId]);

  if (!tenantId || !invoiceId) {
    return <Box color="text-body-secondary">{t('billing.invoiceDetail.missing')}</Box>;
  }

  const lang = i18n.language;

  return (
    <ContentLayout header={<Header variant="h1">{t('billing.invoices.title')}</Header>}>
      <SpaceBetween size="l">
        {invoice.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {invoice.kind === 'NOT_FOUND' && (
          <Box color="text-body-secondary">{t('billing.invoiceDetail.notFound')}</Box>
        )}
        {invoice.kind === 'ERROR' && <Alert type="error">{t('billing.invoiceDetail.errorLoad')}</Alert>}

        {invoice.kind === 'READY' && (
          <>
            <Container
              header={
                <Header
                  variant="h2"
                  description={invoice.data.id}
                  actions={<InvoiceStatusBadge status={invoice.data.status} />}
                >
                  <span style={{ fontFamily: 'monospace' }}>{invoice.data.invoice_number}</span>
                </Header>
              }
            >
              <SpaceBetween size="l">
                <ColumnLayout columns={3} variant="text-grid">
                  <Field label={t('billing.invoiceDetail.issuedAt')} value={formatDate(invoice.data.issued_at, lang)} />
                  <Field label={t('billing.invoiceDetail.dueAt')} value={formatDate(invoice.data.due_at, lang)} />
                  <Field
                    label={t('billing.invoiceDetail.paidAt')}
                    value={invoice.data.paid_at !== null ? formatDate(invoice.data.paid_at, lang) : '—'}
                  />
                  <Field
                    label={t('billing.invoiceDetail.subtotal')}
                    value={<MoneyDisplay amount={invoice.data.subtotal_amount} currency={invoice.data.currency} locale={lang} />}
                  />
                  <Field
                    label={t('billing.invoiceDetail.tax')}
                    value={<MoneyDisplay amount={invoice.data.tax_amount} currency={invoice.data.currency} locale={lang} />}
                  />
                  <Field
                    label={t('billing.invoiceDetail.discount')}
                    value={<MoneyDisplay amount={invoice.data.discount_amount} currency={invoice.data.currency} locale={lang} />}
                  />
                  <Field
                    label={t('billing.invoiceDetail.total')}
                    value={<MoneyDisplay amount={invoice.data.total_amount} currency={invoice.data.currency} locale={lang} />}
                  />
                  <Field
                    label={t('billing.invoiceDetail.amountPaid')}
                    value={<MoneyDisplay amount={invoice.data.amount_paid} currency={invoice.data.currency} locale={lang} />}
                  />
                  <Field
                    label={t('billing.invoiceDetail.amountDue')}
                    value={<MoneyDisplay amount={invoice.data.amount_due} currency={invoice.data.currency} locale={lang} />}
                  />
                </ColumnLayout>
                <InvoiceActions tenantId={tenantId as TenantId} invoice={invoice.data} />
              </SpaceBetween>
            </Container>

            <Container header={<Header variant="h2">{t('billing.invoiceDetail.paymentsTitle')}</Header>}>
              {payments.kind === 'LOADING' && <Spinner />}
              {payments.kind === 'ERROR' && <Alert type="error">{t('billing.payments.errorLoad')}</Alert>}
              {payments.kind === 'READY' && (
                <PaymentTable
                  payments={payments.payments}
                  locale={lang}
                  labels={{
                    paidAt: t('billing.payments.columns.paidAt'),
                    amount: t('billing.payments.columns.amount'),
                    method: t('billing.payments.columns.method'),
                    status: t('billing.payments.columns.status'),
                    provider: t('billing.payments.columns.provider'),
                    empty: t('billing.payments.empty'),
                    notPaid: t('billing.payments.notPaid'),
                    manual: t('billing.payments.manual'),
                  }}
                />
              )}
            </Container>
          </>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}

/** Par rótulo/valor do grid de detalhe. */
function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{value}</div>
    </div>
  );
}
